import { useState, useCallback, useRef, useEffect } from "react";
import { Plus, X, Search, Crown, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CrosswordCell, CrosswordCellRef } from "./CrosswordCell";
import { SearchResults } from "./SearchResults";
import { WordSuggestionForm } from "./WordSuggestionForm";
import { useWordSearchTabs, WordSearchTab } from "@/hooks/useWordSearchTabs";
import { useWordSearch } from "@/hooks/useWordSearch";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const MAX_LENGTH = 30;

interface WordSearchTabContentProps {
  tab: WordSearchTab;
  onUpdateTab: (updates: Partial<Omit<WordSearchTab, "id">>) => void;
  onResultClick?: (word: string) => void;
}

const WordSearchTabContent = ({
  tab,
  onUpdateTab,
  onResultClick,
}: WordSearchTabContentProps) => {
  const [letters, setLetters] = useState<string[]>(() => {
    // Initialize from saved pattern
    const arr = Array(MAX_LENGTH).fill("");
    if (tab.pattern) {
      const cleanPattern = tab.pattern.replace(/\*$/, ""); // Remove trailing *
      for (let i = 0; i < cleanPattern.length && i < MAX_LENGTH; i++) {
        arr[i] = cleanPattern[i] === "?" ? "" : cleanPattern[i];
      }
    }
    return arr;
  });
  const [selectedLength, setSelectedLength] = useState<number | null>(
    tab.selectedLength === 0 ? null : tab.selectedLength
  );
  const cellRefs = useRef<(CrosswordCellRef | null)[]>([]);
  const { searchWords, results, isLoading, error, clearResults } = useWordSearch();

  const [hasSearched, setHasSearched] = useState(false);

  // Sync results from search to tab (including empty results after a search)
  useEffect(() => {
    if (hasSearched) {
      onUpdateTab({ results });
    }
  }, [results, hasSearched]);

  // Sync selectedLength to tab when it changes
  useEffect(() => {
    const newLength = selectedLength ?? 0;
    if (newLength !== tab.selectedLength) {
      onUpdateTab({ selectedLength: newLength });
    }
  }, [selectedLength]);

  const handleLetterChange = useCallback((index: number, value: string) => {
    // If period is entered, set word length to the position before the period
    if (value === '.') {
      setSelectedLength(index > 0 ? index : null);
      return; // Don't add the period to letters
    }
    
    setLetters((prev) => {
      const newLetters = [...prev];
      newLetters[index] = value;
      return newLetters;
    });
  }, []);

  const handleAutoAdvance = useCallback((index: number) => {
    if (index < MAX_LENGTH - 1) {
      cellRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace") {
        if (!letters[index] && index > 0) {
          e.preventDefault();
          setLetters((prev) => {
            const newLetters = [...prev];
            newLetters[index - 1] = "";
            return newLetters;
          });
          cellRefs.current[index - 1]?.focus();
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        cellRefs.current[index - 1]?.focus();
      } else if ((e.key === "ArrowRight" || e.key === " ") && index < MAX_LENGTH - 1) {
        e.preventDefault();
        cellRefs.current[index + 1]?.focus();
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleSearch();
      } else if (e.key === "Home") {
        e.preventDefault();
        cellRefs.current[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        cellRefs.current[MAX_LENGTH - 1]?.focus();
      }
    },
    [letters]
  );

  const handleSearch = () => {
    let pattern: string;

    if (selectedLength === null) {
      // Variable length search - need at least one letter to determine range
      let lastFilledIndex = -1;
      for (let i = MAX_LENGTH - 1; i >= 0; i--) {
        if (letters[i]) {
          lastFilledIndex = i;
          break;
        }
      }

      if (lastFilledIndex === -1) {
        // No letters filled and no length selected - show feedback
        toast.info("Välj en längd eller fyll i minst en bokstav för att söka");
        return;
      }

      pattern =
        letters
          .slice(0, lastFilledIndex + 1)
          .map((l) => l || "?")
          .join("") + "*";
    } else {
      // Fixed length search - can search even without any letters
      pattern = letters
        .slice(0, selectedLength)
        .map((l) => l || "?")
        .join("");
    }

    // Save pattern and length to tab
    onUpdateTab({
      pattern,
      selectedLength: selectedLength ?? 0,
    });

    setHasSearched(true);
    searchWords(pattern, "word", selectedLength);
  };

  const handleReset = () => {
    setLetters(Array(MAX_LENGTH).fill(""));
    clearResults();
    onUpdateTab({ pattern: "", results: [] });
    setTimeout(() => cellRefs.current[0]?.focus(), 50);
  };

  const lengths = Array.from({ length: MAX_LENGTH }, (_, i) => i + 1);

  // Show saved results or current search results
  // After a search is performed, always show current results (even if empty)
  const displayResults = hasSearched ? results : (tab.results || []);

  return (
    <div className="space-y-4">
      {/* Combined grid: radio buttons aligned above input cells */}
      <div className="overflow-x-auto">
        <div className="inline-flex items-start gap-0 min-w-max">
          {/* Clear button column */}
          <div className="flex flex-col items-center">
            <div className="h-5" />
            <div className="h-5" />
            <button
              onClick={handleReset}
              className="w-[26px] h-8 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded transition-colors"
              title="Rensa"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* "?" any length column */}
          <div className="flex flex-col items-center w-[26px]">
            <div className="h-5 flex items-center justify-center text-xs text-muted-foreground font-medium">
              ?
            </div>
            <div className="h-5 flex items-center justify-center">
              <input
                type="radio"
                name={`wordLength-${tab.id}`}
                checked={selectedLength === null}
                onChange={() => setSelectedLength(null)}
                className="w-3 h-3 cursor-pointer accent-primary"
              />
            </div>
            <div className="h-8" />
          </div>

          {/* Number columns with cells */}
          {lengths.map((length, index) => (
            <div key={length} className="flex flex-col items-center w-[26px]">
              <div className="h-5 flex items-center justify-center text-xs text-muted-foreground">
                {length}
              </div>
              <div className="h-5 flex items-center justify-center">
                <input
                  type="radio"
                  name={`wordLength-${tab.id}`}
                  checked={selectedLength === length}
                  onChange={() => setSelectedLength(length)}
                  className="w-3 h-3 cursor-pointer accent-primary"
                />
              </div>
              <CrosswordCell
                ref={(el) => {
                  cellRefs.current[index] = el;
                }}
                value={letters[index]}
                onChange={(value) => handleLetterChange(index, value)}
                onAutoAdvance={() => handleAutoAdvance(index)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                tabIndex={index}
                dimmed={selectedLength !== null && index >= selectedLength}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Search button */}
      <div className="flex justify-center">
        <button
          onClick={handleSearch}
          className={cn(
            "px-12 py-3 rounded-md text-sm font-medium",
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90 active:scale-[0.99]",
            "transition-all duration-150 flex items-center justify-center gap-2"
          )}
        >
          <Search className="w-4 h-4" />
          Sök
        </button>
      </div>

      {/* Results */}
      <SearchResults
        results={displayResults}
        isLoading={isLoading}
        error={error}
        onResultClick={onResultClick}
      />
    </div>
  );
};

interface WordSearchTabsProps {
  onResultClick?: (word: string) => void;
}

export const WordSearchTabs = ({ onResultClick }: WordSearchTabsProps) => {
  const {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    createTab,
    updateTab,
    deleteTab,
    isLoading,
    isAuthenticated,
  } = useWordSearchTabs();

  const { isPremium } = useAuth();

  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll state
  const updateScrollButtons = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", updateScrollButtons);
      window.addEventListener("resize", updateScrollButtons);
      return () => {
        container.removeEventListener("scroll", updateScrollButtons);
        window.removeEventListener("resize", updateScrollButtons);
      };
    }
  }, [tabs, updateScrollButtons]);

  const scrollTabs = (direction: "left" | "right") => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 150;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleCreateTab = async () => {
    const newTab = await createTab();
    if (newTab) {
      setActiveTabId(newTab.id);
    }
  };

  const handleUpdateTab = (updates: Partial<Omit<WordSearchTab, "id">>) => {
    if (activeTabId) {
      updateTab(activeTabId, updates);
    }
  };

  const handleStartRename = (tabId: string, currentName: string) => {
    setEditingTabId(tabId);
    setEditingName(currentName);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const handleFinishRename = () => {
    if (editingTabId && editingName.trim()) {
      updateTab(editingTabId, { name: editingName.trim() });
    }
    setEditingTabId(null);
    setEditingName("");
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleFinishRename();
    } else if (e.key === "Escape") {
      setEditingTabId(null);
      setEditingName("");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground mb-1">Sök efter ord</h2>
        <p className="text-sm text-muted-foreground">
          Fyll i de bokstäver du kan och ange längden på ordet du söker
        </p>
      </div>

      {/* Tab bar with scroll arrows */}
      <div className="relative flex items-center border-b border-border">
        {/* Left scroll arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scrollTabs("left")}
            className="absolute left-0 z-10 flex items-center justify-center w-6 h-full bg-gradient-to-r from-background via-background to-transparent pr-2"
            title="Scrolla vänster"
          >
            <ChevronLeft className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}

        {/* Scrollable tabs container */}
        <div
          ref={tabsContainerRef}
          className="flex items-center gap-1 overflow-x-auto pb-0 scrollbar-hide flex-1"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={cn(
                "group flex items-center gap-1 px-3 py-2 text-sm cursor-pointer rounded-t-md border border-b-0 transition-colors flex-shrink-0",
                activeTabId === tab.id
                  ? "bg-card text-foreground border-border -mb-px"
                  : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground"
              )}
              onClick={() => setActiveTabId(tab.id)}
            >
              {editingTabId === tab.id ? (
                <input
                  ref={editInputRef}
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleFinishRename}
                  onKeyDown={handleRenameKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  className="w-20 px-1 py-0 text-sm bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                  maxLength={20}
                />
              ) : (
                <span
                  className="truncate max-w-[100px]"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    handleStartRename(tab.id, tab.name);
                  }}
                  title="Dubbelklicka för att byta namn"
                >
                  {tab.name}
                </span>
              )}
              {tabs.length > 1 && editingTabId !== tab.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTab(tab.id);
                  }}
                  className={cn(
                    "ml-1 p-0.5 rounded hover:bg-destructive/20 hover:text-destructive transition-colors",
                    "opacity-0 group-hover:opacity-100",
                    activeTabId === tab.id && "opacity-100"
                  )}
                  title="Stäng flik"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}

          {/* Add new tab button - only for premium users */}
          {isPremium && (
            <button
              onClick={handleCreateTab}
              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors flex-shrink-0"
              title="Lägg till sökning"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right scroll arrow */}
        {canScrollRight && (
          <button
            onClick={() => scrollTabs("right")}
            className="absolute right-0 z-10 flex items-center justify-center w-6 h-full bg-gradient-to-l from-background via-background to-transparent pl-2"
            title="Scrolla höger"
          >
            <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Premium upsell for non-premium authenticated users */}
      {isAuthenticated && !isPremium && tabs.length >= 1 && (
        <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
          <Crown className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">Premium-funktion:</span> Skapa flera sökflikar för att arbeta med flera ord samtidigt.
          </span>
        </div>
      )}

      {/* Active tab content */}
      {activeTab && (
        <WordSearchTabContent
          key={activeTab.id}
          tab={activeTab}
          onUpdateTab={handleUpdateTab}
          onResultClick={onResultClick}
        />
      )}

      {/* Word suggestion form */}
      <div className="pt-4 border-t">
        <WordSuggestionForm />
      </div>

      {/* Login prompt for non-authenticated users */}
      {!isAuthenticated && (
        <p className="text-xs text-center text-muted-foreground">
          Logga in för att spara dina sökningar mellan besök
        </p>
      )}
    </div>
  );
};
